import { useEffect, useRef, useState } from "react";
import { apiPost } from "@/lib/apiClient";
import type { MessageRow } from "@shared/types/messages";

export const POLL_INTERVAL_MS = 13_000;

function startOfDayIso(): string {
  const date = new Date();
  date.setHours(0, 0, 0, 0);
  return date.toISOString();
}

export function useMessageQueue() {
  const queueRef = useRef<MessageRow[]>([]);
  const lastFetchRef = useRef<string>(startOfDayIso());
  const [current, setCurrent] = useState<MessageRow | null>(null);

  useEffect(() => {
    let cancelled = false;

    async function fetchMore() {
      try {
        const records = await apiPost<MessageRow[]>("/api/wallboard/messages", {
          since: lastFetchRef.current,
        });
        if (cancelled) return;
        if (records.length > 0) {
          lastFetchRef.current = new Date().toISOString();
          queueRef.current.push(...records);
        }
      } catch (err) {
        // eslint-disable-next-line no-console
        console.error("Failed to fetch messages", err);
      }
    }

    async function tick() {
      if (cancelled) return;
      if (queueRef.current.length === 0) {
        await fetchMore();
      }
      if (cancelled) return;
      const next = queueRef.current.shift() ?? null;
      setCurrent(next);
    }

    void fetchMore();
    const id = window.setInterval(() => {
      void tick();
    }, POLL_INTERVAL_MS);

    return () => {
      cancelled = true;
      window.clearInterval(id);
    };
  }, []);

  const dismiss = () => setCurrent(null);

  return { current, dismiss };
}
